import { ApiError } from '@dodoc/api-client';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatches } from 'react-router';
import { Banner, Button, Checkbox, Drawer, FormField, KeyValue, Select, Textarea } from '@/ui';
import { describeError } from '../api/errors';
import { copyText } from '@/shared/lib/clipboard';
import { sanitizePath, supportSubject, type SupportContext, type SupportTicket, type SupportTopic } from '../api/supportApi';
import { useServices, useSession } from '../AppServicesContext';
import { appConfig } from '../config';
import { sections } from '../registry';
import s from '../layouts/Layout.module.css';
import { useHelp } from './HelpContext';

const MAX_MESSAGE = 2000;

function useScreenContext(details: { requestId?: string | null; errorCode?: string | null }): SupportContext {
  const location = useLocation();
  const matches = useMatches();
  const { state } = useSession();
  const { i18n } = useTranslation();
  const handle = [...matches].reverse().find((match) => (match.handle as { sectionId?: string } | undefined)?.sectionId)?.handle as { sectionId?: string } | undefined;
  return {
    sectionId: handle?.sectionId ?? null,
    path: sanitizePath(location.pathname),
    clinicId: state.clinicId,
    locale: i18n.language,
    appVersion: appConfig.version,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    viewport: typeof window !== 'undefined' ? `${window.innerWidth}×${window.innerHeight}` : '',
    requestId: details.requestId ?? null,
    errorCode: details.errorCode ?? null,
  };
}

/** «Помощь» (ПОРТ-61): обращение в поддержку с техническим контекстом экрана, без данных пациентов. */
export function HelpDrawer() {
  const { t } = useTranslation('shell');
  const { t: tErrors, i18n } = useTranslation('errors');
  const { t: tAny } = useTranslation();
  const { support } = useServices();
  const help = useHelp();
  const context = useScreenContext(help.details);
  const [topic, setTopic] = useState<SupportTopic>('question');
  const [message, setMessage] = useState('');
  const [attachContext, setAttachContext] = useState(true);
  const [touched, setTouched] = useState(false);
  const [ticket, setTicket] = useState<SupportTicket | null>(null);
  const [copied, setCopied] = useState(false);
  const formId = useId();
  const section = sections.find((item) => item.id === context.sectionId) ?? null;
  const screenTitle = section ? tAny(section.titleKey) : typeof document !== 'undefined' ? document.title : '';

  useEffect(() => {
    if (help.isOpen) {
      setTicket(null);
      setTouched(false);
      setCopied(false);
      setTopic(help.details.errorCode ? 'problem' : 'question');
    }
  }, [help.isOpen, help.details.errorCode]);

  const mutation = useMutation({
    mutationFn: () =>
      support.createTicket({
        subject: supportSubject(t(`help.topics.${topic}`), screenTitle),
        message: message.trim(),
        screen: { route: context.path, title: screenTitle },
        ...(attachContext ? { context } : {}),
        topic,
      }),
    onSuccess: (created) => {
      setTicket(created);
      setMessage('');
    },
    meta: { silent: true },
  });

  const messageError = touched && message.trim().length < 10 ? t('help.messageTooShort') : undefined;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setTouched(true);
    if (message.trim().length < 10) return;
    mutation.mutate();
  };

  const contextItems = [
    { id: 'section', term: t('help.context.section'), value: screenTitle || (context.sectionId ?? '') },
    { id: 'path', term: t('help.context.path'), value: context.path },
    { id: 'clinic', term: t('help.context.clinic'), value: context.clinicId ?? '' },
    { id: 'locale', term: t('help.context.locale'), value: context.locale },
    { id: 'version', term: t('help.context.version'), value: context.appVersion },
    { id: 'viewport', term: t('help.context.viewport'), value: context.viewport },
    { id: 'request', term: t('help.context.requestId'), value: context.requestId ?? '' },
  ];

  return (
    <Drawer
      open={help.isOpen}
      onClose={help.close}
      title={t('help.title')}
      width={520}
      footer={
        ticket ? (
          <Button onClick={help.close}>{t('help.done')}</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={help.close}>
              {t('help.cancel')}
            </Button>
            <Button type="submit" form={formId} loading={mutation.isPending}>
              {t('help.submit')}
            </Button>
          </>
        )
      }
    >
      {ticket ? (
        <Banner tone="success" announce title={t('help.sentTitle', { number: ticket.number })}>
          {t('help.sentDescription')}
        </Banner>
      ) : (
        <form id={formId} className={s.helpForm} onSubmit={submit} noValidate>
          <p className={s.helpIntro}>{t('help.intro')}</p>
          {mutation.isError && mutation.error instanceof ApiError && mutation.error.status === 404 ? (
            <Banner
              tone="warning"
              announce
              title={t('help.unavailableTitle')}
              actions={
                <Button size="sm" variant="tertiary" onClick={() => void copyText(message.trim()).then(setCopied)}>
                  {copied ? t('help.copied') : t('help.copyMessage')}
                </Button>
              }
            >
              {t('help.unavailableText')}
            </Banner>
          ) : mutation.isError ? (
            <Banner tone="error" announce title={t('help.errorTitle')}>
              {describeError(mutation.error, tErrors, i18n).description}
            </Banner>
          ) : null}
          <FormField label={t('help.topicLabel')}>
            <Select<SupportTopic>
              value={topic}
              onChange={setTopic}
              options={[
                { value: 'question', label: t('help.topics.question') },
                { value: 'problem', label: t('help.topics.problem') },
                { value: 'idea', label: t('help.topics.idea') },
              ]}
            />
          </FormField>
          <FormField label={t('help.messageLabel')} hint={t('help.messageHint')} error={messageError} required>
            <Textarea value={message} onChange={(event) => setMessage(event.target.value)} onBlur={() => setTouched(true)} maxLength={MAX_MESSAGE} showCount rows={6} />
          </FormField>
          <Checkbox
            label={t('help.attachContext')}
            description={t('help.attachContextHint')}
            checked={attachContext}
            onChange={(event) => setAttachContext(event.target.checked)}
          />
          {attachContext ? (
            <details className={s.helpContext}>
              <summary>{t('help.contextSummary')}</summary>
              <KeyValue items={contextItems} />
            </details>
          ) : null}
        </form>
      )}
    </Drawer>
  );
}
